// Copyright (c) Ping. All rights reserved.
//
// In-call participant card — Figma DS "In call Participant/desktop" (4389:98843).
// Full-bleed camera/avatar, an active-speaker ring + an animated speaking
// equalizer in the bottom-left name tag, a "Be right there" away tag, a raised-
// hand badge, and hover actions (pin / kebab) top-right. Pinning promotes the
// card to the spotlight stage. Web-only (CSS hover + keyframed equalizer — GPU
// transforms, no per-frame JS). The card fills its parent, so the grid /
// filmstrip control the size.

import { memo, useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import { Coffee, MicOff, Pin, PinOff } from 'lucide-react-native';

import { Avatar } from '@ping/ui';

import type { LiveKitParticipantView } from '@calls/livekit/LiveKitCallClient';

import { CallVideoTile } from './CallVideoTile';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const EQ_STYLE_ID = 'ping-call-eq';
function ensureEqKeyframes(): void {
  if (typeof document === 'undefined' || document.getElementById(EQ_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = EQ_STYLE_ID;
  // Four bars bouncing out of phase — pure transform, GPU-composited.
  el.textContent = `
@keyframes ping-eq-a { 0%,100%{transform:scaleY(0.35)} 50%{transform:scaleY(1)} }
@keyframes ping-eq-b { 0%,100%{transform:scaleY(0.6)} 40%{transform:scaleY(0.3)} 70%{transform:scaleY(1)} }
@keyframes ping-eq-c { 0%,100%{transform:scaleY(1)} 50%{transform:scaleY(0.4)} }
@keyframes ping-eq-d { 0%,100%{transform:scaleY(0.5)} 30%{transform:scaleY(1)} 60%{transform:scaleY(0.35)} }`;
  document.head.appendChild(el);
}

/** Animated 4-bar speaking equalizer (Figma "Talking" indicator). */
export function SpeakingEqualizer({ height = 14 }: { height?: number }): JSX.Element {
  useEffect(() => ensureEqKeyframes(), []);
  const anims = ['ping-eq-a', 'ping-eq-b', 'ping-eq-c', 'ping-eq-d'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, height }}>
      {anims.map((a, i) => (
        <span
          key={i}
          style={{
            width: 2,
            height: '100%',
            borderRadius: 999,
            background: '#E8EAED',
            transformOrigin: 'center',
            animation: `${a} 0.9s ease-in-out ${i * 0.12}s infinite`,
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
}

interface ParticipantTileProps {
  readonly p: LiveKitParticipantView;
  readonly speaking: boolean;
  readonly handRaised: boolean;
  readonly away: boolean;
  readonly pinned: boolean;
  /** Stable across renders (parent passes one reference for all tiles) so the
   *  `memo` below actually holds — the tile binds its own identity on press. */
  readonly onTogglePin: (identity: string) => void;
  /** Hide pin/hover actions (e.g. on the spotlight stage itself). */
  readonly hideActions?: boolean;
  /** Smaller name-tag text for the compact filmstrip. */
  readonly compact?: boolean;
  /**
   * Whether this tile may attach a live <video> (decode). Defaults true. When
   * false the camera is suppressed to an avatar to stay under the browser's
   * simultaneous-decoder budget — see videoBudget.ts. The participant is still
   * fully in the call (audio + presence); only their decode is paused.
   */
  readonly allowVideo?: boolean;
  /**
   * Never show this participant's screen-share track here — used by the
   * spotlight filmstrip so the shared screen (already on the stage) isn't
   * decoded a second time as a tile. Falls back to the camera or an avatar.
   */
  readonly cameraOnly?: boolean;
}

// Memoised: in a busy call the parent re-renders often (1s duration tick,
// active-speaker changes, reaction bursts). Without this every tile — and its
// <video> subtree — reconciles each time. With a stable `onTogglePin` the memo
// holds, so a tile only re-renders when its own props actually change.
export const ParticipantTile = memo(function ParticipantTile({
  p,
  speaking,
  handRaised,
  away,
  pinned,
  onTogglePin,
  hideActions = false,
  compact = false,
  allowVideo = true,
  cameraOnly = false,
}: ParticipantTileProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  // Screen shares are always allowed to render (rare + important, and the
  // budget already reserves them); the per-tile cap only gates cameras.
  const screenTrack = cameraOnly ? undefined : p.screenTrack;
  const hasScreen = screenTrack !== undefined;
  const showVideo =
    hasScreen || (allowVideo && p.videoEnabled && p.videoTrack !== undefined);
  const track = screenTrack ?? p.videoTrack;
  const nameSize = compact ? 'text-[10px]' : 'text-[12px]';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <View
        className={`relative h-full w-full overflow-hidden rounded-[12px] bg-white dark:bg-[#1A1C22] ${
          speaking ? 'border-2 border-[#2CA8D4]' : 'border border-black/[0.08] dark:border-white/[0.06]'
        }`}
      >
        {showVideo ? (
          <CallVideoTile track={track} mirror={p.isLocal && !hasScreen} contain={hasScreen} />
        ) : (
          <View className="flex-1 items-center justify-center bg-[#EBEDF0] dark:bg-[#202227]">
            <Avatar size={compact ? 'tile' : 'large'} initials={initials(p.name)} />
          </View>
        )}

        {/* Top-right: raised-hand badge (persistent) + hover actions. */}
        {!hideActions && (handRaised || hovered) ? (
          <View className="absolute right-[8px] top-[8px] flex-row items-center gap-[6px]">
            {handRaised ? (
              <View className="h-[28px] min-w-[28px] items-center justify-center rounded-[10px] bg-[#222429] px-[6px]">
                <Text className="text-[15px]">✋</Text>
              </View>
            ) : null}
            {hovered ? (
              <Pressable
                accessibilityLabel={pinned ? 'Unpin' : 'Pin'}
                onPress={() => onTogglePin(p.identity)}
                className="h-[28px] w-[28px] items-center justify-center rounded-[10px] bg-[#222429] active:opacity-80"
              >
                {pinned ? (
                  <PinOff size={15} color="#E8EAED" />
                ) : (
                  <Pin size={15} color="#E8EAED" />
                )}
              </Pressable>
            ) : null}
          </View>
        ) : null}

        {/* Bottom-left name tag (Figma "In call name tag"). */}
        <View className="absolute bottom-[8px] left-[8px] max-w-[85%] flex-row items-center gap-[4px] rounded-[12px] bg-[#222429] px-2 py-1">
          {away ? (
            <>
              <Coffee size={compact ? 12 : 14} color="#E8EAED" />
              <Text numberOfLines={1} className={`${nameSize} font-bold text-[#F5F6F8]`}>
                Be right there
              </Text>
            </>
          ) : (
            <>
              <Text numberOfLines={1} className={`${nameSize} font-medium text-[#F5F6F8]`}>
                {p.name}
                {p.isLocal ? ' (You)' : ''}
              </Text>
              {speaking ? <SpeakingEqualizer height={compact ? 4 : 6} /> : null}
              {!p.audioEnabled ? <MicOff size={compact ? 12 : 14} color="#FF8A8A" /> : null}
            </>
          )}
        </View>
      </View>
    </div>
  );
});
